<script lang="ts" setup>
import type { DynamicItemSlider } from '@autobid/strapi-integration/rest/components'
import type {
  SearchListSuccess,
} from '@autobid/strapi-integration/typescript/auction/searchCars'
import type { CarImage, SearchListCar } from '@autobid/ui/types/Car'
import CarHoverDetails from '@autobid/ui/components/elements/car/CarHoverDetails.vue'
import {
  useSearchCars,
} from '@autobid/ui/composables/car/data/useSearchCars'
import { useFilterState } from '@autobid/ui/composables/carsList/useFilterState'
import { useQueryClient } from '@tanstack/vue-query'
import { register } from 'swiper/element/bundle'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import Slide from './Slide.vue'

const props = defineProps<DynamicItemSlider & { ids?: number[] }>()

register()

const queryClient = useQueryClient()

const sliderRef = ref()
const isSliderInitialized = ref(false)
const carHoverDetails = ref()
const hoverData = ref({
  car: {} as SearchListCar,
  images: [] as CarImage[],
})
const resumeAutoplayDebounce = ref()
const { $trpc } = useNuxtApp()
const { currentPage, sortingType } = useFilterState()
currentPage.value = 1
sortingType.value = 'auctionStartDate-ASCENDING'

const { data, isFetching, queryKey } = useSearchCars(
  computed(() => props.ids),
  {
    highlighted: props.type === 'highlight',
    pageSize: props.itemsCount,
  },
)

const amountOfFinishedCars = computed(() => {
  if (!data.value)
    return 0

  return data.value.items.filter(el => el.stage === 'FINISHED').length
})

function initSlider() {
  const swiperEl = sliderRef.value

  if (!swiperEl || typeof window === 'undefined') {
    return
  }

  const params = {
    slidesPerView: 3,
    slidesPerGroup: props.rowSlide ? 3 : 1,
    breakpoints: {
      320: {
        slidesPerView: 1,
        ...(props.rowSlide ? { slidesPerGroup: 1 } : {}),
      },
      641: {
        slidesPerView: 2,
        ...(props.rowSlide ? { slidesPerGroup: 2 } : {}),
      },
      1024: {
        slidesPerView: 3,
        ...(props.rowSlide ? { slidesPerGroup: 3 } : {}),
      },
      1280: {
        slidesPerView: 4,
        ...(props.rowSlide ? { slidesPerGroup: 4 } : {}),
      },
    },
    injectStyles: [
      `
      .swiper-button-next,
      .swiper-button-prev{
        color: white;
        font-weight: 800;
        text-shadow: 0px 3px 6px #000000;
        filter: drop-shadow(0 1px 1px #58585a);
      }

      .swiper-button-next::after,
      .swiper-button-prev::after{
        font-size: 3.25rem;
      }

      .swiper-button-prev.swiper-button-disabled,
      .swiper-button-next.swiper-button-disabled{
        pointer-events: auto;
      }
      `,
    ],
  }

  Object.assign(swiperEl, params)

  swiperEl.initialize()
  isSliderInitialized.value = true
}

function showPreview(data: { images: CarImage[], car: SearchListCar }) {
  if (props.autoplay) {
    clearTimeout(resumeAutoplayDebounce.value)
    sliderRef.value.swiper.autoplay.stop()
  }

  hoverData.value = data
  carHoverDetails.value?.showPreview(data.images[0]?.links.l)
}

function hidePreview() {
  if (props.autoplay) {
    resumeAutoplayDebounce.value = setTimeout(() => {
      if (carHoverDetails.value?.previewImgSrc?.length)
        return

      sliderRef.value.swiper.autoplay.start()
    }, 500)
  }

  carHoverDetails.value?.hidePreview()
}

type ConversionSuffix = 'observe'

async function countConversion(carId: number, recommendationReferer:
  | DynamicItemSlider['recommendationReferer']
  | `${DynamicItemSlider['recommendationReferer']}%2B${ConversionSuffix}`) {
  if (props.type !== 'recommendation' || !props.recommendationReferer || !recommendationReferer)
    return

  try {
    await $trpc.webapi.recommendationCarConversion.mutate({
      carId,
      recommendationReferer,
    })
  }
  catch {
    // do nothing
  }
}

onBeforeUnmount(() => {
  clearTimeout(resumeAutoplayDebounce.value)
})

watch(sliderRef, () => {
  initSlider()
})

function updateCarsData() {
  if (!data.value) {
    return
  }

  if (
    amountOfFinishedCars.value >= data.value.items.length - 2
    && data.value.items.length === props.itemsCount
  ) {
    return queryClient.invalidateQueries(queryKey.value)
  }

  // all cars finished
  if (amountOfFinishedCars.value === data.value.items.length) {
    return queryClient.setQueryData(
      queryKey.value,
      (oldData: SearchListSuccess['data']['items'] | undefined): SearchListSuccess['data']['items'] | undefined => {
        if (!oldData) {
          return oldData
        }

        return {
          ...oldData,
          items: [],
        }
      },
    )
  }
}

watch(amountOfFinishedCars, () => {
  updateCarsData()
})
</script>

<template>
  <div>
    <p
      v-if="title"
      class="mb-3 px-3 text-2xl font-bold uppercase text-primary xl:px-0"
    >
      {{ title }}
    </p>

    <div
      v-if="!isSliderInitialized || isFetching"
      class="grid grid-cols-3 gap-3"
    >
      <div
        v-for="i in 3"
        :key="i"
        class="col-span-1 h-[540px] animate-pulse bg-gray-300"
      />
    </div>

    <swiper-container
      v-show="isSliderInitialized && !isFetching"
      :key="type"
      ref="sliderRef"
      class="relative z-10"
      init="false"
      :navigation="true"
      v-bind="
        autoplay
          ? {
            'auto-play': true,
            'autoplay-delay': (playDuration ?? 4) * 1000,
            'autoplay-disable-on-interaction': false,
            'autoplay-pause-on-mouse-enter': true,
          }
          : undefined
      "
      space-between="15"
    >
      <Slide
        v-for="(car, index) in data?.items ?? []"
        :key="`${type}-${car.id}`"
        :car="car"
        :index="index"
        :type="type"
        :recommendation-referer="recommendationReferer"
        @show-preview="showPreview"
        @hide-preview="hidePreview"
        @move-to-barometer="
          countConversion(car.id, props.recommendationReferer)
        "
        @observe="
          countConversion(car.id, `${props.recommendationReferer}%2Bobserve`)
        "
      />
    </swiper-container>

    <CarHoverDetails
      ref="carHoverDetails"
      :car="hoverData.car"
      :images="hoverData.images"
      centered
      @move-to-barometer="
        countConversion(hoverData.car.id, props.recommendationReferer)
      "
      @move-to-details="
        countConversion(hoverData.car.id, props.recommendationReferer)
      "
    />
  </div>
</template>
